import {useIntl} from 'react-intl';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import {useInputs} from '@bellawatt/use-inputs';
import {Box, makeStyles, Typography, useTheme} from '@material-ui/core';
import {HorizontalBar} from 'react-chartjs-2';
import {formatAsThousands, formatAsDollars} from '@generic/functions/formatters';
import times from 'lodash/times';

const useStyles = makeStyles(() => ({
  chartHeader: {
    fontWeight: '600',
  },
}));

export default function PlanningRatesOverviewChart({annualCosts}) {
  const classes = useStyles();
  const theme = useTheme();
  const {formatMessage} = useIntl();
  const {includeSCCRebate, separatelyMetered} = useInputs();

  const CHARGERS_COLORS = times(annualCosts.length, () => theme.palette.primary.main);

  const data = {
    labels: annualCosts.map(({title}) => title),
    datasets: [
      {
        label: formatMessage({id: 'planning.content.electricity.rateBreakdown.facility'}),
        data: annualCosts.map(({facility}) => facility),
        barPercentage: 0.7,
        backgroundColor: theme.palette.secondary.lighter,
      },
      {
        label: formatMessage({id: 'planning.content.electricity.rateBreakdown.chargers'}),
        data: annualCosts.map(({chargers, siteSccRebate: {total}}) => chargers - total),
        barPercentage: 0.7,
        backgroundColor: CHARGERS_COLORS,
      },
      {
        label: formatMessage({id: 'smartChargeCommercial.graphLabel'}),
        data: annualCosts.map(({siteSccRebate: {total}}) => total),
        barPercentage: 0.7,
        borderColor: theme.palette.success.main,
        borderWidth: 1,
        backgroundColor: theme.palette.neutral.light,
      },
    ],
  };

  const options = {
    legend: {
      display: true,
      labels: {
        filter: (legendItem) => {
          const isSccRebate =
            legendItem.text === formatMessage({id: 'smartChargeCommercial.graphLabel'});
          const isFacility =
            legendItem.text ===
            formatMessage({id: 'planning.content.electricity.rateBreakdown.facility'});
          if (isSccRebate && !includeSCCRebate) {
            return false;
          }
          if (isFacility && separatelyMetered) {
            return false;
          }
          return true;
        },
      },
    },
    maintainAspectRatio: false,
    tooltips: {
      callbacks: {
        label: (dataItem, context) => {
          const {datasetIndex, index} = dataItem;
          const value = context.datasets[datasetIndex].data[index];
          // don't show zero values
          return value > 0
            ? `${context.datasets[dataItem.datasetIndex].label}: $${formatAsThousands(value)}`
            : null;
        },
      },
    },
    scales: {
      yAxes: [
        {
          stacked: true,
          gridLines: {
            display: false,
          },
        },
      ],
      xAxes: [
        {
          stacked: true,
          ticks: {
            beginAtZero: true,
            maxTicksLimit: 8,
            callback: (value) => `$${formatAsThousands(value)}`,
          },
        },
      ],
    },
    layout: {
      padding: {
        right: 100,
      },
    },
    plugins: {
      datalabels: {
        formatter: (_value, ctx) => {
          const {datasets} = ctx.chart.data;
          const {
            siteSccRebate: {total},
          } = annualCosts[ctx.dataIndex];
          if (ctx.datasetIndex === datasets.length - 1 && total > 0) {
            return `${formatAsDollars(total)} incentive`;
          }
          return '';
        },
        align: 'end',
        anchor: 'end',
        color: theme.palette.success.main,
        font: {
          weight: 'bold',
        },
      },
    },
  };

  const plugins = [ChartDataLabels];

  return (
    <Box mt={6}>
      <Typography align="center" variant="h6" className={classes.chartHeader}>
        {formatMessage(
          {id: 'charging.content.costs.electricity.rateBreakdown.cheapestRateTitle'},
          {rateTitle: annualCosts[0].title}
        )}
      </Typography>
      <Typography align="center">
        {formatMessage(
          {
            id: 'smartChargeCommercial.chartSubtitle.text',
          },
          {
            link: (
              <a
                href={formatMessage({id: 'smartChargeCommercial.chartSubtitle.href'})}
                target="_blank"
                rel="noreferrer"
              >
                {formatMessage({id: 'smartChargeCommercial.chartSubtitle.linkTrigger'})}
              </a>
            ),
          }
        )}
      </Typography>
      <Box height={300} mt={3}>
        <HorizontalBar data={data} options={options} plugins={plugins} />
      </Box>
    </Box>
  );
}
